import { AxiosError } from 'axios';

/**
 * Standard API error response structure.
 */
export interface ApiErrorResponse {
  message?: string | Record<string, string[]>;
  error?: string;
  status?: number;
}

/**
 * Extract error message from Axios error.
 * @param error - The Axios error
 * @param fallback - Fallback message
 */
export function getErrorMessage(error: unknown, fallback: string = 'An error occurred'): string {
  if (error instanceof AxiosError) {
    const response = error.response?.data as ApiErrorResponse | undefined;
    const message = response?.message;

    // message can be an object with field validation errors (ListStringMapErrorDto)
    // Format: { fieldName: ["error1", "error2"], ... }
    if (message && typeof message === 'object') {
      const allErrors = Object.values(message).flat();
      if (allErrors.length > 0) {
        return allErrors.join('; ');
      }
    }

    if (typeof message === 'string' && message) {
      return message;
    }

    return response?.error || error.message || fallback;
  }

  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  return fallback;
}

/**
 * Parse Blob error response back to JSON.
 * Use in catch blocks of requests with responseType: 'blob',
 * where error responses are JSON but Axios wraps them as Blob.
 */
export async function parseBlobError(error: unknown): Promise<void> {
  if (error instanceof AxiosError && error.response?.data instanceof Blob) {
    try {
      const text = await error.response.data.text();
      error.response.data = JSON.parse(text);
    } catch {
      // Not valid JSON — leave as is
    }
  }
}

/**
 * Check if error is a specific HTTP status.
 * @param error - The error to check
 * @param status - The HTTP status code
 */
export function isHttpError(error: unknown, status: number): boolean {
  if (error instanceof AxiosError) {
    return error.response?.status === status;
  }
  return false;
}

/**
 * Check if error is unauthorized (401).
 */
export function isUnauthorized(error: unknown): boolean {
  return isHttpError(error, 401);
}

/**
 * Check if error is forbidden (403).
 */
export function isForbidden(error: unknown): boolean {
  return isHttpError(error, 403);
}

/**
 * Check if error is not found (404).
 */
export function isNotFound(error: unknown): boolean {
  return isHttpError(error, 404);
}

/**
 * Wrapper for async functions with standardized error handling.
 * @param fn - The async function to wrap
 * @param onError - Error callback
 */
export async function withErrorHandling<T>(
  fn: () => Promise<T>,
  onError?: (message: string, error: unknown) => void
): Promise<T | null> {
  try {
    return await fn();
  } catch (error) {
    const message = getErrorMessage(error);
    onError?.(message, error);
    return null;
  }
}
